import { useState, useRef } from 'react';
import { MediaItem } from '../types';
import { getApiUrl } from '../config/api';
import { handleSaveMedia } from '../components/VideoEditor/projectHandlers';

export type QualityLevel = 'sd' | 'hd' | '2k' | 'fhd' | '4k';

interface UseVideoProcessingProps {
  projectId: string;
  audioUrl: string | null;
  videoUrl?: string | null;  // Add videoUrl for video-only mode
  aspectRatio: string;
  isConversionMode: boolean;
  targetAspectRatio: string;
  includeWatermark: boolean;
  showNotification: (notification: any) => void;
}

export const useVideoProcessing = ({
  projectId,
  audioUrl,
  videoUrl,
  aspectRatio,
  isConversionMode,
  targetAspectRatio,
  includeWatermark,
  showNotification
}: UseVideoProcessingProps) => {
  const [isProcessing, setIsProcessing] = useState({
    saveAndDownloadMedia: false,
    savingMedia: false,
    downLoadMedia: false,
    autoGenerateLibrary: false
  });
  const [isPreparingDownload, setIsPreparingDownload] = useState<boolean>(false);
  const [mediaJobId, setMediaJobId] = useState<string | null>(null);
  const [showMediaDashboard, setShowMediaDashboard] = useState<boolean>(false);

  const generateMedia = async (
    mediaItems: MediaItem[],
    save: boolean,
    quality?: QualityLevel,
    onMediaItemsUpdate?: (items: MediaItem[]) => void
  ) => {
    console.log('🚀 generateMedia called:', { 
      save, 
      quality,
      audioUrl: !!audioUrl,
      mediaItemsCount: mediaItems.length,
      projectId 
    });
    
    if (!audioUrl && !videoUrl) {
      console.warn('⚠️ No audioUrl or videoUrl found, returning early');
      return;
    }

    try {
      if (save) {
        setIsProcessing((prev) => ({ ...prev, saveAndDownloadMedia: true }));
      } else {
        setIsProcessing((prev) => ({ ...prev, downLoadMedia: true }));
      }

      setShowMediaDashboard(false);

      let savedItems = null;
      if (save) {
        // Use audioUrl if available (audio mode), otherwise use videoUrl (video mode)
        const mediaUrl = audioUrl || videoUrl;
        savedItems = await handleSaveMedia(mediaItems, projectId, mediaUrl!);
        console.log("======================>mediaUrl", mediaUrl);
        console.log("======================> save media items done", savedItems);
        if (onMediaItemsUpdate && savedItems) {
          onMediaItemsUpdate(savedItems);
        }
      }

      // Filter out hidden media items before sending to backend
      let tempMedia = savedItems || mediaItems;
      const visibleMediaItems = tempMedia.filter(item => !item.hidden);
      const cleanedItems = visibleMediaItems.map(item => ({ 
        ...item, 
        length: item.end - item.start 
      }));
      
      console.log('🔍 Media filtering for generation:', {
        totalItems: tempMedia.length,
        hiddenItems: tempMedia.filter(item => item.hidden).length,
        visibleItems: visibleMediaItems.length
      });

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Create watermark config if enabled
      const watermarkConfig = includeWatermark ? {
        id: 'watermark-' + Date.now(),
        isWatermark: true,
        itemType: 'watermark' as const,
        start: 0,
        text: 'KingdomStudio.pro',
        style: {
          fontSize: 30,
          color: '#ffffff40',
          stroke: '#00000040',
          strokeWidth: 1,
          fontFamily: 'Anton',
          shadowColor: '#00000000',
          shadowX: 0,
          textAlign: 'center' as const,
          xPosition: 82.45865835359116,
          yPosition: 6.977886977886977
        }
      } : undefined;

      const response = await fetch(getApiUrl("/media/create"), {
        method: "POST",
        headers,
        body: JSON.stringify({
          projectId,
          audioUrl: audioUrl || undefined,
          mediaItems: cleanedItems,
          aspectRatio: isConversionMode ? targetAspectRatio : aspectRatio,
          quality: quality || 'hd',
          watermark: watermarkConfig
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to start media creation: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.jobId) {
        throw new Error("No job ID returned from server");
      }

      setMediaJobId(result.jobId);
      setShowMediaDashboard(true);
      
      console.log('🎯 Dashboard should show:', {
        jobId: result.jobId,
        showDashboard: true,
        hasJobId: !!result.jobId
      });

      // showNotification({ 
      //   type: 'success', 
      //   message: 'Media creation job submitted successfully!', 
      //   title: 'Success' 
      // });

      return result.jobId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('❌ Media generation failed:', error);
      showNotification({ 
        type: 'error', 
        message: `Failed to generate media: ${errorMessage}`, 
        title: 'Error' 
      });
      throw error;
    } finally {
      if (save) {
        setIsProcessing((prev) => ({ ...prev, saveAndDownloadMedia: false }));
      } else {
        setIsProcessing((prev) => ({ ...prev, downLoadMedia: false }));
      }
    }
  };

  return {
    isProcessing,
    setIsProcessing,
    isPreparingDownload,
    setIsPreparingDownload,
    mediaJobId,
    setMediaJobId,
    showMediaDashboard,
    setShowMediaDashboard,
    generateMedia
  };
};
